/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Normalize a website URL coming from Airtable so it always carries an
 * explicit `https?:` / `mailto:` scheme.
 *
 * Airtable text fields commonly hold bare hosts ("partner.com",
 * "www.partner.com") with no `https://` prefix. Handing those to React
 * directly renders `<a href="partner.com">`, which the browser resolves as a
 * same-origin relative link — clicking it on `unrealengine.com/training`
 * navigates to `unrealengine.com/training/partner.com` and 404s.
 *
 * Returns:
 * - `undefined` for null/empty/whitespace input
 * - the original string unchanged if it already starts with `http://` /
 *   `https://` / `mailto:`
 * - `https:` prepended to a protocol-relative `//host` so downstream
 *   `target="_blank"`/`rel="noopener"` detection always sees an absolute URL
 * - `https://` + the input otherwise
 */
export function normalizeWebsiteUrl(url: string | undefined | null): string | undefined {
	if (!url) return undefined;
	const trimmed = url.trim();
	if (!trimmed) return undefined;
	if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
	// Promote `//host` → `https://host` so the renderer always sees a
	// scheme-qualified URL and applies the right external-link safety attrs.
	if (trimmed.startsWith("//")) return `https:${trimmed}`;
	return `https://${trimmed}`;
}

/**
 * Turn a human display name into a URL-safe slug for a detail-page path
 * segment (e.g. `"Acme Studios, Inc."` → `"acme-studios-inc"`).
 *
 * Lowercases, strips diacritics (`Café` → `cafe`), replaces every run of
 * non-alphanumeric characters with a single dash, and trims leading/trailing
 * dashes. Returns `""` for input that has no alphanumeric content — callers
 * that key a route off the slug should guard against the empty string.
 */
export function slugify(name: string): string {
	return (
		name
			.normalize("NFKD")
			// Strip the combining diacritical marks NFKD splits off (U+0300–U+036F).
			.replace(/[̀-ͯ]/g, "")
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-+|-+$/g, "")
	);
}

/**
 * Join a directory page's URL path to a child segment with exactly one `/`,
 * regardless of whether `base` carries a trailing slash. Used to build a
 * service partner's detail-page URL from the live directory path + the
 * partner's slug — kept here so the path-composition rule lives next to
 * `slugify`, its only producer of segments.
 */
export function joinDirectoryPath(base: string, segment: string): string {
	return `${base.replace(/\/+$/, "")}/${segment}`;
}
